import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Send, Mail, Phone, Clock, CheckCircle2 } from "lucide-react";
import { api } from "@/lib/api";
import { Seo } from "@/components/Seo";
import { Reveal } from "@/components/Reveal";
import { useLanguage } from "../i18n";

const SERVICE_OPTIONS = [
  "Realizzazione siti web",
  "Redesign siti web",
  "Presenza digitale",
  "Recensioni NFC",
  "Altro",
];

const BUDGET_OPTIONS = ["Preferisco non indicarlo", "Fino a 1.000 €", "1.000 – 3.000 €", "3.000 – 6.000 €", "Oltre 6.000 €"];

const EMPTY = { name: "", email: "", company: "", service_type: "", budget: "", message: "", privacy: false, website: "" };

export default function Contatti() {
  const [form, setForm] = useState(EMPTY);
  const [settings, setSettings] = useState(null);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const { t } = useLanguage();

  useEffect(() => {
    api.get("/settings/public").then((r) => setSettings(r.data)).catch(() => {});
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.privacy) {
      toast.error(t("Devi accettare l'informativa privacy per inviare la richiesta."));
      return;
    }
    setSending(true);
    try {
      await api.post("/contact", form);
      setSent(true);
      toast.success(t("Richiesta inviata con successo."));
    } catch {
      toast.error(t("Qualcosa è andato storto. Riprova tra poco."));
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <Seo
        title={`${t("Contatti")} — Alsera Studio`}
        description={t("Ogni grande progetto inizia con una conversazione. Raccontaci cosa hai in mente: ti aiuteremo a individuare i prossimi passi.")}
      />
      <section className="pt-32 pb-24 sm:pt-40" data-testid="contatti-section">
        <div className="mx-auto grid max-w-7xl gap-14 px-5 sm:px-8 lg:grid-cols-[0.9fr_1.1fr]">
          <Reveal>
            <p className="eyebrow">{t("Contatti")}</p>
            <h1 className="mt-4 text-4xl font-extrabold leading-[1.1] tracking-tight text-antracite sm:text-5xl">
              {t("Parliamo del tuo progetto.")}
            </h1>
            <p className="mt-6 max-w-md text-base leading-relaxed text-antracite/70 sm:text-lg">
              {t("Ogni grande progetto inizia con una conversazione. Raccontaci cosa hai in mente: ti aiuteremo a individuare i prossimi passi.")}
            </p>
            <ul className="mt-10 space-y-5" data-testid="contatti-recapiti">
              {settings?.contact_email && (
                <li className="flex items-center gap-3">
                  <span className="rounded-2xl bg-acqua p-3 text-petrolio"><Mail className="h-5 w-5" /></span>
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-[0.14em] text-antracite/50">{t("Email")}</p>
                    <a href={`mailto:${settings.contact_email}`} className="text-sm font-semibold text-petrolio hover:underline" data-testid="contatti-email">
                      {settings.contact_email}
                    </a>
                  </div>
                </li>
              )}
              {settings?.phone && (
                <li className="flex items-center gap-3">
                  <span className="rounded-2xl bg-acqua p-3 text-petrolio"><Phone className="h-5 w-5" /></span>
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-[0.14em] text-antracite/50">{t("Telefono")}</p>
                    <a href={`tel:${settings.phone.replace(/\s/g, "")}`} className="text-sm font-semibold text-petrolio hover:underline" data-testid="contatti-phone">
                      {settings.phone}
                    </a>
                  </div>
                </li>
              )}
              {settings?.response_time && (
                <li className="flex items-center gap-3">
                  <span className="rounded-2xl bg-acqua p-3 text-petrolio"><Clock className="h-5 w-5" /></span>
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-[0.14em] text-antracite/50">{t("Tempi di risposta")}</p>
                    <p className="text-sm font-semibold text-antracite/80" data-testid="contatti-response-time">{settings.response_time}</p>
                  </div>
                </li>
              )}
              {!settings?.contact_email && !settings?.phone && (
                <li className="rounded-2xl bg-acqua/50 p-5 text-sm leading-relaxed text-antracite/70">
                  {t("Il modo più rapido per contattarci è il modulo qui accanto: ogni richiesta viene letta e gestita personalmente.")}
                </li>
              )}
            </ul>
          </Reveal>

          <Reveal delay={0.15}>
            <div className="card-soft noise-overlay p-8 sm:p-10">
              {sent ? (
                <div className="flex flex-col items-start gap-4 py-10" data-testid="contact-success">
                  <span className="rounded-full bg-acqua p-4 text-petrolio">
                    <CheckCircle2 className="h-8 w-8" />
                  </span>
                  <h2 className="text-2xl font-bold tracking-tight text-antracite">{t("Richiesta inviata.")}</h2>
                  <p className="max-w-md text-base leading-relaxed text-antracite/70">
                    {t("Grazie per averci scritto. La tua richiesta è stata registrata: ti ricontatteremo presto per parlare del tuo progetto.")}
                  </p>
                  <button type="button" onClick={() => { setForm(EMPTY); setSent(false); }} className="btn-secondary" data-testid="contact-send-another">
                    {t("Invia un'altra richiesta")}
                  </button>
                </div>
              ) : (
                <form onSubmit={submit} className="space-y-5" data-testid="contact-form">
                  <div className="grid gap-5 sm:grid-cols-2">
                    <div>
                      <label htmlFor="contact-name" className="mb-1.5 block text-xs font-bold uppercase tracking-[0.12em] text-antracite/60">{t("Nome e cognome *")}</label>
                      <input id="contact-name" data-testid="contact-name" required value={form.name} onChange={(e) => set("name", e.target.value)} className="input-field" placeholder="Mario Rossi" />
                    </div>
                    <div>
                      <label htmlFor="contact-email" className="mb-1.5 block text-xs font-bold uppercase tracking-[0.12em] text-antracite/60">{t("Email")} *</label>
                      <input id="contact-email" data-testid="contact-email" type="email" required value={form.email} onChange={(e) => set("email", e.target.value)} className="input-field" placeholder="mario@esempio.it" />
                    </div>
                  </div>
                  <div className="grid gap-5 sm:grid-cols-2">
                    <div>
                      <label htmlFor="contact-company" className="mb-1.5 block text-xs font-bold uppercase tracking-[0.12em] text-antracite/60">{t("Azienda")}</label>
                      <input id="contact-company" data-testid="contact-company" value={form.company} onChange={(e) => set("company", e.target.value)} className="input-field" placeholder={t("Facoltativo")} />
                    </div>
                    <div>
                      <label htmlFor="contact-service" className="mb-1.5 block text-xs font-bold uppercase tracking-[0.12em] text-antracite/60">{t("Servizio di interesse")}</label>
                      <select id="contact-service" data-testid="contact-service" value={form.service_type} onChange={(e) => set("service_type", e.target.value)} className="input-field">
                        <option value="">{t("Facoltativo")}</option>
                        {SERVICE_OPTIONS.map((o) => <option key={o} value={o}>{t(o)}</option>)}
                      </select>
                    </div>
                  </div>
                  <div>
                    <label htmlFor="contact-budget" className="mb-1.5 block text-xs font-bold uppercase tracking-[0.12em] text-antracite/60">{t("Budget indicativo")}</label>
                    <select id="contact-budget" data-testid="contact-budget" value={form.budget} onChange={(e) => set("budget", e.target.value)} className="input-field">
                      <option value="">{t("Facoltativo")}</option>
                      {BUDGET_OPTIONS.map((o) => <option key={o} value={o}>{t(o)}</option>)}
                    </select>
                  </div>
                  <div>
                    <label htmlFor="contact-message" className="mb-1.5 block text-xs font-bold uppercase tracking-[0.12em] text-antracite/60">{t("Descrizione del progetto *")}</label>
                    <textarea id="contact-message" data-testid="contact-message" required rows={5} value={form.message} onChange={(e) => set("message", e.target.value)} className="input-field resize-none" placeholder={t("Raccontaci la tua idea, i tuoi obiettivi e cosa vorresti realizzare…")} />
                  </div>
                  <div aria-hidden="true" style={{ position: "absolute", left: "-9999px", height: 0, overflow: "hidden" }}>
                    <label htmlFor="contact-website">Sito web</label>
                    <input id="contact-website" type="text" data-testid="contact-website-honeypot" value={form.website} onChange={(e) => set("website", e.target.value)} tabIndex={-1} autoComplete="off" />
                  </div>
                  <label className="flex items-start gap-3 text-sm text-antracite/70" data-testid="contact-privacy-label">
                    <input type="checkbox" data-testid="contact-privacy" checked={form.privacy} onChange={(e) => set("privacy", e.target.checked)} className="mt-1 h-4 w-4 rounded border-salvia/60 accent-petrolio" />
                    <span>{t("Ho letto l'informativa privacy e acconsento al trattamento dei miei dati per essere ricontattato. *")}</span>
                  </label>
                  <button type="submit" disabled={sending} className="btn-primary w-full disabled:opacity-60" data-testid="contact-submit">
                    <Send className="h-4 w-4" />
                    {sending ? t("Invio in corso…") : t("Invia la tua richiesta")}
                  </button>
                </form>
              )}
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
